import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/**
 * UseWakeLock is a hook that allows you to request a wake lock on the screen.
 * It will automatically release the wake lock when the component unmounts or the enabled prop is false.
 * It will also release the wake lock when the tab is hidden or the browser is put into fullscreen mode.
 *
 * @param enabled - Whether to request a wake lock.
 */
export function useWakeLock({ enabled = true, } = {}) {
    const isSupported = useMemo(() => 'wakeLock' in navigator, []);
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState(null);
    const wakeLockRef = useRef(null);
    useEffect(() => {
        async function requestPermission() {
            await navigator.permissions.query({
                name: 'screen-wake-lock',
            });
        }
        requestPermission();
    }, []);
    const requestWakeLock = useCallback(async () => {
        if (!isSupported || !enabled || wakeLockRef.current) {
            return;
        }
        try {
            wakeLockRef.current = await navigator.wakeLock.request();
            setIsActive(true);
            setError(null);
            wakeLockRef.current.addEventListener('release', () => {
                // Check if the release was intentional (due to enabled=false or unmount)
                // If wakeLockRef.current is null, it means release was called manually
                if (wakeLockRef.current) {
                    setIsActive(false);
                    wakeLockRef.current = null;
                }
            });
        }
        catch (err) {
            console.error('Failed to acquire wake lock:', err);
            setError(err instanceof Error
                ? err
                : new Error('Failed to acquire wake lock'));
            setIsActive(false);
            wakeLockRef.current = null;
        }
    }, [isSupported, enabled]);
    const releaseWakeLock = useCallback(async () => {
        if (wakeLockRef.current) {
            const lock = wakeLockRef.current;
            wakeLockRef.current = null; // Mark as intentionally released
            await lock.release();
            setIsActive(false);
        }
    }, []);
    useEffect(() => {
        if (!isSupported) {
            return;
        }
        if (enabled) {
            requestWakeLock();
        }
        else {
            releaseWakeLock();
        }
        // Re-request lock if tab becomes visible again after being hidden
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && enabled) {
                requestWakeLock();
            }
            else if (document.visibilityState === 'hidden') {
                // Release lock when tab is hidden, browser might do this anyway
                releaseWakeLock();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // Also listen for fullscreen changes as wake lock might be released
        document.addEventListener('fullscreenchange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('fullscreenchange', handleVisibilityChange);
            // Ensure lock is released on unmount or if 'enabled' becomes false
            releaseWakeLock();
        };
    }, [isSupported, enabled, requestWakeLock, releaseWakeLock]);
    return { isSupported, isActive, error, requestWakeLock, releaseWakeLock };
}
